/**
 * This function exports data to a CSV file.
 * It takes an array of objects and converts it to CSV format.
 * Then it creates a Blob from the CSV string and generates a download link.
 * Finally, it programmatically clicks the link to trigger the download.
 *
 * @param {Array} data - The data to be exported, should be an array of objects.
 * @param {string} fileName - The name of the file to be downloaded (without extension).
 * @returns {void}
 */
const UTF8_BOM = "\uFEFF";
const CSV_MIME_TYPE = "text/csv;charset=utf-8;";

const createCsvBlob = (csv) =>
  new Blob([UTF8_BOM + csv], { type: CSV_MIME_TYPE });

const downloadCsvBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportToCsv = (data, fileName) => {
  if (!data || data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvRows = [];
  csvRows.push(headers.join(","));

  for (const row of data) {
    const values = headers.map((header) => {
      const raw = row[header] ?? "";
      const sanitized = sanitizeValue(String(raw).replace(/"/g, '""'));
      return `"${sanitized}"`;
    });
    csvRows.push(values.join(","));
  }

  const csv = csvRows.join("\n");
  downloadCsvBlob(createCsvBlob(csv), fileName);
};

/**
 * Converts an array of objects to a CSV string.
 * @param {Array<Object>} data - Array of objects to convert
 * @returns {string} CSV string representation
 */
export const convertToCSV = (data) => {
  const headers = Object.keys(data[0]);
  const csvRows = [];

  csvRows.push(headers.join(","));

  for (const row of data) {
    const values = headers.map((header) => {
      const escaped = ("" + row[header]).replace(/"/g, '""');
      return `"${escaped}"`;
    });
    csvRows.push(values.join(","));
  }

  return csvRows.join("\n");
};

/**
 * Exports data to CSV with custom headers and renaming, with data sanitization.
 * @param {Array} data - Array of objects to export.
 * @param {Array} headers - Keys to include in export, in order.
 * @param {Array} renamedHeaders - Column names for headers.
 * @param {string} fileName - Name of the file (without extension).
 */
export const exportToCsvWithHeaders = (
  data,
  headers,
  renamedHeaders,
  fileName,
) => {
  const csvRows = [];
  csvRows.push(renamedHeaders.join(","));

  for (const row of data) {
    const values = headers.map((header) => {
      let raw = row[header] ?? "";
      if (typeof raw === "object" && raw !== null) {
        if (header === "dismissBy") {
          raw = raw.fullName ?? ""; // export the full name only
        } else if (header === "licensee") {
          raw = raw.name ?? ""; // export the name only
        } else {
          raw = JSON.stringify(raw); // fallback for other objects
        }
      }
      const sanitized = sanitizeValue(String(raw).replace(/"/g, '""'));
      return `"${sanitized}"`;
      //const raw = row[header] ?? "";
      //const sanitized = sanitizeValue(String(raw).replace(/"/g, '""'));
      //return `"${sanitized}"`;
    });

    csvRows.push(values.join(","));
  }

  const csv = csvRows.join("\n");
  downloadCsvBlob(createCsvBlob(csv), fileName);
};

const sanitizeValue = (value) => {
  if (typeof value !== "string") return value;
  // Remove script tags and trim
  return value.replace(/<script.*?>.*?<\/script>/gi, "").trim();
};
